import styles from './CreditsPaymentMethodUpdate.module.scss';
import { LoadingPanel } from 'src/pages/PaymentBetaPage/components/LoadingPanel';
import { stripePromise } from 'src/utils';
import { Elements } from '@stripe/react-stripe-js';
import { useColorTheme, useSession, useVisible } from 'src/hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { StripeElementsForPaymentMethodUpdate } from 'src/v2/pages/subscription/components/CreditsPaymentMethodUpdate/components/StripeElementsForPaymentMethodUpdate';
import { PanelHeader } from 'src/v2/pages/subscription/components/CreditsCheckout/components/PanelHeader';
import { useSetupIntentMutation } from 'src/store/services';
import { toast } from 'react-toastify';

const errorMessage =
  'We’re having trouble processing your update. Please check your information or try a different payment method.';

type CreditsPaymentMethodUpdateProps = {
  onClose: () => void;
  onUpdatePaymentProcessing: (value: boolean) => void;
};

export const CreditsPaymentMethodUpdate = ({
  onClose,
  onUpdatePaymentProcessing,
}: CreditsPaymentMethodUpdateProps) => {
  const { isDarkTheme } = useColorTheme();
  const {
    appUser: { user_id },
  } = useSession();
  const [isReadyStripeElements, setReadyStripeElements] =
    useState<boolean>(false);
  const [setupIntentClientSecret, setSetupIntentClientSecret] =
    useState<string>('');
  const [isSetupIntentFailed, setIsSetupIntentFailed] =
    useState<boolean>(false);
  const hasRequestedSetupIntentRef = useRef<boolean>(false);
  const [setupIntent] = useSetupIntentMutation();

  const {
    isVisible: isElementsLoading,
    onVisibilityRemove: onElementsLoadingRemove,
    onVisibilitySet: onElementsLoadingSet,
  } = useVisible(true);

  const requestSetupIntent = useCallback(async () => {
    try {
      const result = await setupIntent({ user_id }).unwrap();
      setSetupIntentClientSecret(result.client_secret);
      setIsSetupIntentFailed(false);
      setReadyStripeElements(false);
      return result.client_secret;
    } catch (error) {
      setIsSetupIntentFailed(true);
      toast.error(errorMessage);
      return null;
    }
  }, [setupIntent, user_id]);

  useEffect(() => {
    if (
      (setupIntentClientSecret && isReadyStripeElements) ||
      isSetupIntentFailed
    ) {
      onElementsLoadingRemove();
    } else {
      onElementsLoadingSet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadyStripeElements, setupIntentClientSecret, isSetupIntentFailed]);

  useEffect(() => {
    if (
      setupIntentClientSecret ||
      isSetupIntentFailed ||
      hasRequestedSetupIntentRef.current
    ) {
      return;
    }

    let isMounted = true;
    hasRequestedSetupIntentRef.current = true;

    const fetchSetupIntent = async () => {
      const clientSecret = await requestSetupIntent();

      if (!isMounted || clientSecret) {
        if (!isMounted) {
          return;
        }
        return;
      }
    };

    fetchSetupIntent();

    return () => {
      isMounted = false;
      hasRequestedSetupIntentRef.current = false;
    };
  }, [isSetupIntentFailed, requestSetupIntent, setupIntentClientSecret]);

  const handleRefreshSetupIntent = async () => {
    hasRequestedSetupIntentRef.current = false;
    setSetupIntentClientSecret('');
    setIsSetupIntentFailed(false);
    await requestSetupIntent();
  };

  return (
    <div className={styles.root}>
      {isElementsLoading && (
        <LoadingPanel
          onClose={onClose}
          title="Update payment card"
          classname={styles.loadingScreen}
        />
      )}
      <PanelHeader onClose={onClose} title="Update payment card" />
      <div className={styles.mainContainer}>
        {stripePromise && setupIntentClientSecret && (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret: setupIntentClientSecret,
              appearance: {
                theme: isDarkTheme ? 'night' : 'stripe',
                variables: {
                  borderRadius: '12px',
                },
              },
            }}
          >
            <StripeElementsForPaymentMethodUpdate
              onReady={() => setReadyStripeElements(true)}
              onUpdatePaymentProcessing={onUpdatePaymentProcessing}
              onUpdateAccountFailed={handleRefreshSetupIntent}
            />
          </Elements>
        )}
      </div>
    </div>
  );
};
